/**
 * tRPC Client - Cấu hình kết nối tRPC từ frontend đến backend
 *
 * Sử dụng @trpc/tanstack-react-query (tRPC v11 recommended)
 * Pattern: SPA singleton (Vite) - không cần React context
 * Ref: https://trpc.io/docs/client/tanstack-react-query/setup#3c-set-up-without-react-context
 *
 * Tích hợp với:
 * - TanStack Query thông qua @trpc/tanstack-react-query
 * - Auth store (Zustand) để tự động gắn JWT token
 * - Token refresh tự động khi nhận 401
 * - Smart Header Sync Indicator tự động cho 100% POST mutations
 */

import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { isGatewayDownStatus, markServiceUpdating } from './service-status';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import superjson from 'superjson';
import type { AppRouter } from '@apv-erp/trpc';
import { useAuthStore } from '@/stores/auth.store';
import { useSyncStore } from '@/stores/sync.store';
import { queryClient } from './query-client';
import { tokenManager } from './token-manager';
import { recordFailedRequest, registerAutoReportSender } from './diagnostics';
import { getTraceHeaders, recordApiEvent } from './event-journal';

export { queryClient } from './query-client';

/** Sự kiện phát ra sau mỗi mutation tRPC thành công (xem StandardDetailLayout / ActivityLogSection) */
export const DATA_MUTATED_EVENT = 'apv:data-mutated';

const SILENT_TRPC_PROCEDURE_PREFIXES = [
  'auth.login',
  'auth.logout',
  'auth.refresh',
  'auth.forgotPassword',
  'auth.resetPassword',
  'auth.me',
  'feedback.reportAuto',
  'userEvents.',
] as const;

export function isSilentBackgroundProcedure(urlStr: string): boolean {
  try {
    const pathPart = urlStr.split('?')[0] || '';
    const trpcIndex = pathPart.indexOf('/trpc/');
    const rawProcedures = trpcIndex >= 0 ? pathPart.slice(trpcIndex + 6) : pathPart;
    const procedures = rawProcedures
      .split(',')
      .map((proc) => proc.trim())
      .filter(Boolean);
    if (procedures.length === 0) {
      return false;
    }
    return procedures.every((proc) =>
      SILENT_TRPC_PROCEDURE_PREFIXES.some((prefix) =>
        prefix.endsWith('.') ? proc.startsWith(prefix) : proc === prefix,
      ),
    );
  } catch {
    return false;
  }
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3006/api';

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_BASE_URL}/trpc`,
      transformer: superjson,

      // Tự động gắn JWT token vào mỗi request
      async headers() {
        const token = useAuthStore.getState().accessToken;
        return {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
      },

      // Xử lý lỗi 401, tự động refresh token và đồng bộ Header Sync cho 100% POST mutations
      async fetch(url, rawOptions) {
        const startedAt = Date.now();
        const urlStr = url.toString();
        const method = (rawOptions?.method || 'GET').toUpperCase();
        const shouldTrackSync = method === 'POST' && !isSilentBackgroundProcedure(urlStr);

        if (shouldTrackSync) {
          useSyncStore.getState().beginTask('Đang lưu dữ liệu...');
        }

        // Chuẩn Feedback v2: mỗi request có x-request-id mới + x-session-id của tab
        const trace = getTraceHeaders();
        const tracedHeaders = new Headers(rawOptions?.headers as HeadersInit | undefined);
        tracedHeaders.set('x-request-id', trace['x-request-id']);
        tracedHeaders.set('x-session-id', trace['x-session-id']);
        const options = { ...(rawOptions || {}), headers: tracedHeaders } as typeof rawOptions;
        let response: Response;
        try {
          response = await fetch(url, options);
        } catch (netErr: unknown) {
          if (shouldTrackSync) {
            useSyncStore.getState().completeTask(false);
          }
          const errMessage = netErr instanceof Error ? netErr.message : 'Network Error';
          recordApiEvent({
            method: options?.method || 'GET',
            url: urlStr,
            status: 'NETWORK_ERROR',
            durationMs: Date.now() - startedAt,
            requestId: trace['x-request-id'],
            message: errMessage,
          });
          recordFailedRequest({
            method: options?.method || 'GET',
            url: urlStr,
            status: 'NETWORK_ERROR',
            durationMs: Date.now() - startedAt,
            message: errMessage,
          });
          throw netErr;
        }

        const durationMs = Date.now() - startedAt;
        // Máy chủ đang cập nhật (nginx trả 502/503/504) → hiện thông báo "đang cập nhật" thay cho lỗi từng trang
        if (isGatewayDownStatus(response.status)) markServiceUpdating();
        recordApiEvent({
          method: options?.method || 'GET',
          url: urlStr,
          status: response.status,
          durationMs,
          requestId: response.headers.get('x-request-id') || trace['x-request-id'],
        });

        // Detect 401: tRPC batch trả về HTTP 500 nhưng body chứa UNAUTHORIZED
        let isUnauthorized = response.status === 401;
        let parsedBody: unknown = null;
        if (!response.ok) {
          try {
            const cloned = response.clone();
            parsedBody = await cloned.json();
            const results = Array.isArray(parsedBody) ? parsedBody : [parsedBody];
            if (!isUnauthorized && response.status === 500) {
              isUnauthorized = results.some((r: unknown) => {
                const errData = (
                  r as { error?: { json?: { data?: { code?: string; httpStatus?: number } } } }
                )?.error?.json?.data;
                return errData?.code === 'UNAUTHORIZED' || errData?.httpStatus === 401;
              });
            }
          } catch {
            /* ignore parse errors */
          }
        }

        // Bỏ qua xử lý 401 nếu đang gọi api login
        if (urlStr.includes('auth.login')) {
          isUnauthorized = false;
        }

        let finalResponse = response;
        if (isUnauthorized) {
          const newToken = await tokenManager.refreshAccessToken();

          if (newToken) {
            // Retry với token mới
            const newHeaders = new Headers(options?.headers);
            newHeaders.set('Authorization', `Bearer ${newToken}`);
            try {
              finalResponse = await fetch(url, { ...options, headers: newHeaders });
            } catch (retryErr: unknown) {
              if (shouldTrackSync) {
                useSyncStore.getState().completeTask(false);
              }
              throw retryErr;
            }
          }
        }

        if (!finalResponse.ok && !isUnauthorized) {
          const results = Array.isArray(parsedBody) ? parsedBody : parsedBody ? [parsedBody] : [];
          const errEntry = (
            results.find((r: unknown) => Boolean((r as { error?: unknown })?.error)) as
              | { error?: { json?: { message?: string; data?: { httpStatus?: number } }; message?: string } }
              | undefined
          )?.error;
          const errMsg = errEntry?.json?.message || errEntry?.message || finalResponse.statusText;
          const errStatus = errEntry?.json?.data?.httpStatus || finalResponse.status;
          recordFailedRequest({
            method: options?.method || 'GET',
            url: urlStr,
            status: errStatus,
            durationMs,
            message: errMsg,
            requestId: finalResponse.headers.get('x-request-id') || trace['x-request-id'],
          });
        }

        let hasRpcError = !finalResponse.ok;
        if (shouldTrackSync) {
          if (!hasRpcError) {
            try {
              const bodyJson: unknown = await finalResponse.clone().json();
              const batchItems = Array.isArray(bodyJson) ? bodyJson : [bodyJson];
              hasRpcError = batchItems.some((item: unknown) =>
                Boolean(item && typeof item === 'object' && (item as { error?: unknown }).error),
              );
            } catch {
              hasRpcError = false;
            }
          }
          useSyncStore.getState().completeTask(!hasRpcError);
        }

        // Mutation thành công → báo cho footer "Tạo/Cập nhật" và tab "Lịch sử cập nhật" tải lại
        // (server ghi nhật ký trước khi trả kết quả, nên đọc ngay là thấy).
        if (finalResponse.ok && !hasRpcError && method === 'POST' && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(DATA_MUTATED_EVENT));
        }

        return finalResponse;
      },
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});

// Đăng ký hàm gửi phiếu lỗi tự động từ trình duyệt về server
registerAutoReportSender(async (payload) => {
  return (trpcClient as unknown as { feedback: { reportAuto: { mutate: (p: unknown) => Promise<unknown> } } }).feedback.reportAuto.mutate(payload);
});
